import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPermissions,
  fetchPermissionsById,
} from "slices/permissions/thunk";
import { Accordion, Form, Button } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { Axios } from "api/axios";
import { clippingParents } from "@popperjs/core";
import SubmitButton from "Components/Buttons/SubmitButton";
import { useHandleBack } from "helpers/functions/useHandleBack";

interface PermissionTabProps {
  id?: string;
}

const PermissionTab: React.FC<PermissionTabProps> = ({ id }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();
  const [openAccordion, setOpenAccordion] = useState<string | undefined>();
  const [selected, setSelected] = useState<any>({});
  const [hasExistingPermissions, setHasExistingPermissions] = useState(false);
  const handleBack = useHandleBack("/user-list");
  const { modules } = useSelector((state: any) => state.Permission);

  const local_data = JSON.parse(
    localStorage?.getItem("permission_user") || "{}"
  );
  const user = Object?.keys(local_data)?.length > 0 ? local_data : id;
  const authUser = JSON.parse(localStorage?.getItem("authUser") || "{}");

  useEffect(() => {
    dispatch(fetchPermissions());
  }, [dispatch]);

  useEffect(() => {
    const local_data = JSON.parse(
      localStorage.getItem("permission_user") || "{}"
    );

    const isCreating = !id && Object.keys(local_data).length === 0;

    if (isCreating) {
      setSelected({});
      setHasExistingPermissions(false);
      setOpenAccordion(undefined);
    }
    return () => {
      localStorage.removeItem("permission_user");
    };
  }, []);

  useEffect(() => {
    if (user && modules.length > 0) {
      dispatch(fetchPermissionsById(user))
        .unwrap()
        .then((resp: any) => {
          const userPermissions = resp?.apps_permissions || [];
          if (userPermissions.length > 0) {
            setHasExistingPermissions(true);
          } else {
            setHasExistingPermissions(false);
          }

          const normalized: any = {};
          let firstFullyCheckedApp: string | undefined;
          modules.forEach((modApp: any) => {
            const appName = modApp.app_name;
            normalized[appName] = {};

            const matchedApp = userPermissions.find(
              (ap: any) => ap.apps_name === appName
            );

            let appFullyChecked = true;

            Object.entries(modApp?.module_name_perm || {}).forEach(
              ([moduleName, actions]: [string, any]) => {
                normalized[appName][moduleName] = {};
                const matchedModule = matchedApp?.modules_permissions?.find(
                  (m: any) => m.modules_name === moduleName
                );

                let moduleFullyChecked = true;

                actions.forEach((action: string) => {
                  const isChecked =
                    matchedModule?.permissions?.[action] || false;
                  normalized[appName][moduleName][action] = isChecked;

                  if (!isChecked) {
                    moduleFullyChecked = false;
                    appFullyChecked = false;
                  }
                });

                if (moduleFullyChecked && !openAccordion) {
                  setOpenAccordion(appName);
                }
              }
            );

            if (appFullyChecked && !firstFullyCheckedApp) {
              firstFullyCheckedApp = appName;
            }
          });

          setSelected(normalized);

          // Open app accordion if entire app fully checked
          if (firstFullyCheckedApp && !openAccordion) {
            setOpenAccordion(firstFullyCheckedApp);
          }
        })
        .catch(() => {
          setHasExistingPermissions(false);
          // toast.error("Failed to fetch user permissions");
        });
    }
  }, [user, modules, dispatch]);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? undefined : id);
  };

  // const handlePermissionChange = (
  //   app: string,
  //   module: string,
  //   action: string,
  //   checked: boolean
  // ) => {
  //   setSelected((prev: any) => {
  //     const updated = structuredClone(prev || {});

  //     if (!updated[app]) updated[app] = {};
  //     if (!updated[app][module]) updated[app][module] = {};
  //     updated[app][module][action] = checked;
  //     return updated;
  //   });
  // };

  const handlePermissionChange = (
    app: string,
    module: string,
    action: string,
    checked: boolean
  ) => {
    setSelected((prev: any) => {
      const updated = structuredClone(prev || {});
      if (!updated[app]) updated[app] = {};
      if (!updated[app][module]) updated[app][module] = {};

      updated[app][module][action] = checked;

      // Ensure all parent states are properly updated
      const appData = modules.find((m: any) => m.app_name === app);
      if (appData) {
        // Update module state
        const moduleActions = appData.module_name_perm[module] || [];
        const moduleFullyChecked = moduleActions.every(
          (act: string) => updated[app][module][act] === true
        );

        // If module is fully checked, ensure all its actions are checked
        if (moduleFullyChecked) {
          moduleActions.forEach((act: string) => {
            updated[app][module][act] = true;
          });
        }

        // Update app state
        const appFullyChecked = Object.keys(appData.module_name_perm).every(
          (mod) => {
            const modActions = appData.module_name_perm[mod] || [];
            return modActions.every(
              (act: string) => updated[app]?.[mod]?.[act] === true
            );
          }
        );

        // If app is fully checked, ensure all its modules and actions are checked
        if (appFullyChecked) {
          Object.keys(appData.module_name_perm).forEach((mod) => {
            if (!updated[app][mod]) updated[app][mod] = {};
            appData.module_name_perm[mod].forEach((act: string) => {
              updated[app][mod][act] = true;
            });
          });
        }
      }

      return updated;
    });
  };

  const handleAppAllChange = (app: string, checked: boolean) => {
    const appData = modules.find((mod: any) => mod.app_name === app);

    setSelected((prev: any) => {
      const updated = structuredClone(prev || {});
      updated[app] = {};

      Object.entries(appData.module_name_perm).forEach(
        ([modName, actions]: [string, any]) => {
          updated[app][modName] = {};
          actions.forEach((action: string) => {
            updated[app][modName][action] = checked;
          });
        }
      );

      return updated;
    });
  };

  const handleModuleAllChange = (
    app: string,
    module: string,
    checked: boolean
  ) => {
    setSelected((prev: any) => {
      // const updated = { ...prev };
      const updated = structuredClone(prev || {});

      updated[app] = updated[app] || {};
      updated[app][module] = {};
      const actions =
        modules.find((m: any) => m.app_name === app)?.module_name_perm[
          module
        ] || [];
      actions.forEach((action: string) => {
        updated[app][module][action] = checked;
      });
      return updated;
    });
  };

  const handleGlobalAllChange = (checked: boolean) => {
    const updated: any = {};
    modules.forEach((app: any) => {
      updated[app.app_name] = {};
      Object.entries(app.module_name_perm).forEach(
        ([modName, actions]: [string, any]) => {
          updated[app.app_name][modName] = {};
          actions.forEach((action: string) => {
            updated[app.app_name][modName][action] = checked;
          });
        }
      );
    });
    setSelected(updated);
  };

  const isModuleFullyChecked = (app: string, module: string) => {
    const appData = modules.find((m: any) => m.app_name === app);
    const actions = appData?.module_name_perm?.[module] || [];

    if (!selected?.[app] || !selected[app][module] || actions.length === 0) {
      return false;
    }

    return actions.every(
      (action: string) => selected[app][module]?.[action] === true
    );
  };

  const isAppFullyChecked = (app: string) => {
    const appData = modules.find((m: any) => m.app_name === app);
    if (!selected?.[app]) {
      return false;
    }
    return Object.keys(appData?.module_name_perm || {}).every((modName) =>
      isModuleFullyChecked(app, modName)
    );
  };

  const isGlobalFullyChecked = () => {
    if (!modules || modules.length === 0) {
      return false;
    }
    return modules.every((app: any) => isAppFullyChecked(app.app_name));
  };

  const updatePermision = async (payload: any) => {
    try {
      const update = await Axios.put(
        `api/permissions/permissions_list/update/${user}/`,
        payload
      );
      console.log(update.data);
      return update.data;
    } catch (error) {
      console.error(error);
    }
  };

  const createPermision = async (payload: any) => {
    try {
      const create = await Axios.post(
        `api/permissions/permissions_list/create/`,
        payload
      );
      return create.data;
    } catch (error) {
      console.error(error);
    }
  };

  const [submitLoading, setSubmitLoading] = useState(false);

  const handleSubmit = async () => {
    const apps_permissions = Object.entries(selected).map(
      ([apps_name, modulesObj]: any) => ({
        apps_name,
        modules_permissions: Object.entries(modulesObj).map(
          ([modules_name, permissions]: any) => ({
            modules_name,
            permissions,
          })
        ),
      })
    );

    const payload = {
      user: user || "",
      created_by: authUser?.user_id,
      updated_by: authUser?.user_id,
      apps_permissions,
    };

    console.log(payload);

    let response = null;
    setSubmitLoading(true);
    // if (hasExistingPermissions) {
    //   response = await updatePermision(payload);
    // } else {
    //   response = await createPermision(payload);
    // }

    //*// Fallback to update if create fails
    response = await createPermision(payload);
    if (!response || response.status !== 200) {
      response = await updatePermision(payload);
    }

    if (response !== null) {
      toast.success(response?.message);
      setTimeout(() => {
        setSubmitLoading(false);
        handleBack();
      }, 1000);
    } else {
      toast.error(
        `Unable to ${hasExistingPermissions ? "update" : "create"} permission`
      );
    }
  };

  return (
    <div className="mt-4 px-4 bg-white py-2 rounded-4 shadow-sm">
      <h5 className="mb-3 mt-3">Permissions</h5>
      {/* <ToastContainer /> */}

      <Form.Group className="mb-3" controlId="globalAll">
        <Form.Check
          type="checkbox"
          label="All Permissions"
          checked={isGlobalFullyChecked()}
          onChange={(e) => handleGlobalAllChange(e.target.checked)}
        />
      </Form.Group>

      {modules.map((app: any, idx: number) => (
        <div key={idx} className="mb-3">
          <Accordion key={idx} activeKey={openAccordion}>
            <Accordion.Item eventKey={app.app_name}>
              <Accordion.Header onClick={() => toggleAccordion(app.app_name)}>
                {app.app_name}
              </Accordion.Header>
              <Accordion.Body>
                <Form.Group className="mb-2">
                  <Form.Check
                    type="checkbox"
                    label="All Permissions"
                    checked={isAppFullyChecked(app.app_name)}
                    onChange={(e) =>
                      handleAppAllChange(app.app_name, e.target.checked)
                    }
                  />
                </Form.Group>
                {Object.entries(app?.module_name_perm || {}).map(
                  ([modName, actions]: [string, any], i: number) => (
                    <div key={i} className="ms-3 mt-3">
                      <strong>{modName}</strong>
                      <div className="ms-3 mb-2">
                        <Form.Check
                          type="checkbox"
                          label="All Actions"
                          checked={isModuleFullyChecked(app.app_name, modName)}
                          onChange={(e) =>
                            handleModuleAllChange(
                              app.app_name,
                              modName,
                              e.target.checked
                            )
                          }
                        />
                      </div>
                      <div className="ms-3 d-flex flex-wrap gap-3 mt-2">
                        {actions.map((action: string, j: number) => (
                          <Form.Check
                            key={j}
                            type="checkbox"
                            inline
                            label={action}
                            checked={
                              selected?.[app.app_name]?.[modName]?.[action] ||
                              false
                            }
                            onChange={(e) =>
                              handlePermissionChange(
                                app.app_name,
                                modName,
                                action,
                                e.target.checked
                              )
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )
                )}
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>
      ))}

      <div className="d-flex justify-content-center gap-3 py-3">
        <Button
          variant="outline-secondary"
          style={{
            color: "#878899",
            borderColor: "#B0BAC9",
            backgroundColor: "transparent",
          }}
          onClick={handleBack}
        >
          Cancel
        </Button>
        {/* <Button variant="primary" onClick={handleSubmit}>
          {hasExistingPermissions ? "Update" : "Create"}
        </Button> */}

        <SubmitButton
          isSubmitting={submitLoading}
          isEdit={hasExistingPermissions}
          onClick={handleSubmit}
          labels={{
            create: "Create",
            update: "Update",
            submitting: "Saving...",
            updating: "Updating...",
          }}
        />
      </div>
    </div>
  );
};

export default PermissionTab;
